import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Archive,
  Database,
  FileJson,
  FileSpreadsheet,
  FileText,
  LoaderCircle,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { getSocket } from '../../lib/socket';
import type { DataArchiveEvent, InsightCluster, InsightStatus } from '../../types';
import {
  getDataArchiveSummary,
  getInsightStatus,
  purgeArchivedDataset,
  startInsightReport,
} from './dataApi';
import { ModulePage } from './ModulePage';

const numberFormatter = new Intl.NumberFormat('en-US');
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function DataArchive() {
  const queryClient = useQueryClient();
  const [selectedReport, setSelectedReport] = useState<{ id: number; room: string } | null>(null);
  const summary = useQuery({
    queryKey: queryKeys.dataSummary,
    queryFn: ({ signal }) => getDataArchiveSummary(signal),
  });
  const purge = useMutation({
    mutationFn: purgeArchivedDataset,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.dataSummary });
    },
  });

  useEffect(() => {
    const socket = getSocket();
    const reportReady = (payload: { eventId: number }) => {
      if (Number.isInteger(payload.eventId)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.dataReport(payload.eventId) });
      }
    };
    socket.on('report_ready', reportReady);
    return () => { socket.off('report_ready', reportReady); };
  }, [queryClient]);

  const removeDataset = (event: DataArchiveEvent) => {
    if (!event.archived || purge.isPending) return;
    const room = event.room_name || event.topic || `Event ${event.id}`;
    const approved = window.confirm(
      `Permanently delete ${room}'s dataset (${formatNumber(event.messages)} messages + replies)?\n\nThis cannot be undone. Download an export first if you need a copy.`,
    );
    if (approved) purge.mutate(event.id);
  };

  const data = summary.data;
  return (
    <ModulePage
      id="data-archive-title"
      title="Data"
      className="data-archive"
      innerClassName="data-archive__inner"
      meta={data ? <span className="data-archive__count">{formatNumber(data.totals.messages)} messages</span> : null}
      actions={(
        <button
          className="icon-button data-archive__refresh"
          type="button"
          title="Refresh archive"
          aria-label="Refresh archive"
          disabled={summary.isFetching}
          onClick={() => void summary.refetch()}
        >
          <RefreshCw className={summary.isFetching ? 'spin' : ''} size={17} />
        </button>
      )}
    >
      {summary.isPending ? (
          <div className="native-module__state" role="status"><LoaderCircle className="spin" size={20} />Loading archive</div>
        ) : null}

        {summary.isError ? (
          <div className="native-module__state native-module__state--error" role="alert">
            <AlertCircle size={22} />
            <strong>Archive could not be loaded</strong>
            <span>{errorMessage(summary.error, 'Try refreshing the archive.')}</span>
            <button className="secondary-button" type="button" onClick={() => void summary.refetch()}>Try again</button>
          </div>
        ) : null}

        {data ? (
          <>
            <div className="data-stats" aria-label="Archive totals">
              <Stat label="Events with chat" value={data.totals.withChat} suffix={`/ ${formatNumber(data.totals.events)}`} />
              <Stat label="Messages" value={data.totals.messages} />
              <Stat label="Reply pairs" value={data.totals.replyPairs} />
              <Stat label="Training rows" value={data.totals.messages} />
            </div>

            <section className="data-corpus" aria-labelledby="data-corpus-title">
              <div className="data-corpus__copy">
                <Database size={19} aria-hidden="true" />
                <div>
                  <h2 id="data-corpus-title">Full corpus</h2>
                  <p>Every event, message, and moderator reply in one export.</p>
                </div>
              </div>
              <div className="data-corpus__actions">
                <a className="primary-button" href="/api/data/export-all?format=json"><FileJson size={16} />JSON</a>
                <a className="secondary-button" href="/api/data/export-all?format=csv"><FileSpreadsheet size={16} />CSV</a>
              </div>
              <code>modiq.chat.v1</code>
            </section>

            <section className="data-events" aria-labelledby="data-events-title">
              <header className="data-events__header">
                <div>
                  <h2 id="data-events-title">Per-event datasets</h2>
                  <span>{formatNumber(data.events.length)} events</span>
                </div>
              </header>

              {data.events.length === 0 ? (
                <div className="native-module__state data-events__empty">
                  <Archive size={23} />
                  <strong>No chat captured yet</strong>
                  <span>Event datasets will appear here after chat is stored.</span>
                </div>
              ) : (
                <div className="data-events__table-wrap">
                  <table>
                    <caption>Available event chat datasets</caption>
                    <thead>
                      <tr>
                        <th scope="col">Room</th>
                        <th scope="col">Type</th>
                        <th scope="col">Chat window</th>
                        <th scope="col">Messages</th>
                        <th scope="col">Replies</th>
                        <th scope="col">Download</th>
                        <th scope="col">Insights</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.events.map(event => (
                        <DataEventRow
                          event={event}
                          key={event.id}
                          purgeError={purge.isError && purge.variables === event.id ? errorMessage(purge.error, 'Could not delete this dataset.') : null}
                          purging={purge.isPending && purge.variables === event.id}
                          onOpenReport={() => setSelectedReport({ id: event.id, room: event.room_name || event.topic || `Event ${event.id}` })}
                          onPurge={() => removeDataset(event)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
      ) : null}

      {selectedReport ? (
        <InsightsDialog eventId={selectedReport.id} room={selectedReport.room} onClose={() => setSelectedReport(null)} />
      ) : null}
    </ModulePage>
  );
}

function Stat({ label, value, suffix }: { label: string; value: number; suffix?: string }) {
  return (
    <div className="data-stat">
      <span>{label}</span>
      <strong>{formatNumber(value)} {suffix ? <small>{suffix}</small> : null}</strong>
    </div>
  );
}

function DataEventRow({
  event,
  purgeError,
  purging,
  onOpenReport,
  onPurge,
}: {
  event: DataArchiveEvent;
  purgeError: string | null;
  purging: boolean;
  onOpenReport: () => void;
  onPurge: () => void;
}) {
  const room = event.room_name || event.topic || `Event ${event.id}`;
  const hasMessages = event.messages > 0;
  return (
    <tr>
      <th scope="row">
        <span className="data-event__room">{room}</span>
        <span className="data-event__id">ID {event.zoom_id}</span>
      </th>
      <td data-label="Type">
        <span className="data-chip">{event.event_type || '-'}</span>
        {event.archived ? <span className="data-chip data-chip--archived">Archived</span> : null}
      </td>
      <td data-label="Chat window">{formatDate(event.first_ts || event.last_ts)}</td>
      <td data-label="Messages" className="data-event__number">{formatNumber(event.messages)}</td>
      <td data-label="Replies" className="data-event__number">{formatNumber(event.reply_pairs)}</td>
      <td data-label="Download">
        <div className="data-event__downloads">
          <DownloadLink enabled={hasMessages} eventId={event.id} format="json" label="JSON"><FileJson size={14} /></DownloadLink>
          <DownloadLink enabled={hasMessages} eventId={event.id} format="csv" label="CSV"><FileSpreadsheet size={14} /></DownloadLink>
          <DownloadLink enabled={hasMessages} eventId={event.id} format="txt" label="Text"><FileText size={14} /></DownloadLink>
          {event.archived ? (
            <button className="icon-button data-event__purge" type="button" title="Delete archived dataset" aria-label={`Delete ${room} dataset`} disabled={purging} onClick={onPurge}>
              {purging ? <LoaderCircle className="spin" size={15} /> : <Trash2 size={15} />}
            </button>
          ) : null}
        </div>
        {purgeError ? <span className="data-event__error" role="alert">{purgeError}</span> : null}
      </td>
      <td data-label="Insights"><InsightActions event={event} onOpen={onOpenReport} /></td>
    </tr>
  );
}

function DownloadLink({
  enabled,
  eventId,
  format,
  label,
  children,
}: {
  enabled: boolean;
  eventId: number;
  format: 'json' | 'csv' | 'txt';
  label: string;
  children: React.ReactNode;
}) {
  if (!enabled) return <span className="data-event__download disabled" aria-disabled="true">{children}<span>{label}</span></span>;
  return <a className="data-event__download" href={`/api/events/${eventId}/export?format=${format}`}>{children}<span>{label}</span></a>;
}

function InsightActions({ event, onOpen }: { event: DataArchiveEvent; onOpen: () => void }) {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: queryKeys.dataReport(event.id),
    queryFn: ({ signal }) => getInsightStatus(event.id, signal),
    enabled: event.messages > 0,
    refetchInterval: query => query.state.data?.status === 'building' ? 5_000 : false,
  });
  const generate = useMutation({
    mutationFn: (refresh: boolean) => startInsightReport(event.id, refresh),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.dataReport(event.id) });
    },
  });

  if (event.messages === 0) return <span className="data-insight data-insight--muted">No chat</span>;
  if (status.isPending) return <span className="data-insight data-insight--muted"><LoaderCircle className="spin" size={13} />Checking</span>;
  if (status.isError) {
    return <button className="data-insight data-insight--error" type="button" onClick={() => void status.refetch()}>Retry status</button>;
  }

  const value = status.data;
  if (value.status === 'ready') {
    return (
      <div className="data-insight__ready">
        <button className="data-insight data-insight--ready" type="button" onClick={onOpen}><Sparkles size={14} />View</button>
        <a href={`/api/data/report/${event.id}/brief`} title="Download brief">Brief</a>
        <a href={`/api/data/report/${event.id}/outreach`} title="Download follow-ups">Follow-ups</a>
        {value.aiReady ? (
          <button className="icon-button data-insight__redo" type="button" title="Regenerate insights" aria-label="Regenerate insights" disabled={generate.isPending} onClick={() => generate.mutate(true)}>
            <RefreshCw className={generate.isPending ? 'spin' : ''} size={13} />
          </button>
        ) : null}
      </div>
    );
  }
  if (!value.aiReady) return <span className="data-insight data-insight--muted">AI off</span>;
  if (value.status === 'building' || generate.isPending) return <span className="data-insight data-insight--building"><LoaderCircle className="spin" size={13} />Building</span>;
  return (
    <div className="data-insight__stack">
      <button className="data-insight data-insight--action" type="button" onClick={() => generate.mutate(value.status === 'error')}>
        <Sparkles size={14} />{value.status === 'error' ? 'Retry' : 'Generate'}
      </button>
      {value.error ? <span className="data-event__error">{value.error}</span> : null}
      {generate.isError ? <span className="data-event__error">{errorMessage(generate.error, 'Could not start insights.')}</span> : null}
    </div>
  );
}

function InsightsDialog({ eventId, room, onClose }: { eventId: number; room: string; onClose: () => void }) {
  const status = useQuery({
    queryKey: queryKeys.dataReport(eventId),
    queryFn: ({ signal }) => getInsightStatus(eventId, signal),
  });
  const value = status.data;
  const report = value?.report;

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div className="insight-dialog" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="insight-dialog__sheet" role="dialog" aria-modal="true" aria-labelledby="insight-dialog-title">
        <header className="insight-dialog__header">
          <div>
            <p>Room insights</p>
            <h2 id="insight-dialog-title">{value?.room || room}</h2>
          </div>
          <div className="insight-dialog__actions">
            {report ? <a className="secondary-button" href={`/api/data/report/${eventId}/brief`} title="Download brief" aria-label="Download brief"><FileText size={15} />Brief</a> : null}
            {report ? <a className="secondary-button" href={`/api/data/report/${eventId}/outreach`} title="Download follow-ups" aria-label="Download follow-ups"><FileSpreadsheet size={15} />Follow-ups</a> : null}
            <button className="icon-button" type="button" title="Close insights" aria-label="Close insights" onClick={onClose}><X size={18} /></button>
          </div>
        </header>
        <div className="insight-dialog__body">
          {status.isPending ? <div className="native-module__state"><LoaderCircle className="spin" size={20} />Loading insights</div> : null}
          {status.isError ? <div className="native-module__state native-module__state--error" role="alert">{errorMessage(status.error, 'Could not load insights.')}</div> : null}
          {value && !report ? <div className="native-module__state">No stored insights are ready for this room.</div> : null}
          {value && report ? <InsightReportBody status={value} /> : null}
        </div>
      </section>
    </div>
  );
}

function InsightReportBody({ status }: { status: InsightStatus }) {
  const report = status.report;
  if (!report) return null;
  const facts = report.facts || status.facts;
  return (
    <>
      <div className="insight-summary">
        <span><strong>{formatNumber(facts.messages)}</strong> lines</span>
        <span><strong>{formatNumber(facts.questions)}</strong> questions</span>
        <span><strong>{formatNumber(facts.unanswered)}</strong> unanswered</span>
        <span><strong>{formatNumber(facts.uniquePeople)}</strong> people</span>
      </div>
      {report.truncated ? <p className="insight-note">Analysed {formatNumber(report.analysedMessages)} prioritized chat lines.</p> : null}
      {report.sentiment ? <p className="insight-mood">Mood: <strong>{report.sentiment.overall}</strong><span>{report.sentiment.note}</span></p> : null}
      <ClusterSection title="Top questions" clusters={report.topQuestions} showPeople />
      <ClusterSection title="What they asked for" clusters={report.requests} showPeople />
      <ClusterSection title="Buying signals" clusters={report.buying} showPeople />
      <ClusterSection title="Questions about paying" clusters={report.money} showPeople />
      <ClusterSection title="What they want" clusters={report.wants} />
      {report.important.length ? (
        <section className="insight-section">
          <h3>Answer these people</h3>
          {report.important.map((item, index) => (
            <article className="insight-block" key={`${item.who.messageId}-${index}`}>
              <header><strong>{item.question}</strong>{item.who.answered ? null : <span>Unanswered</span>}</header>
              <p>{item.whyItMatters}</p>
              <small>{item.who.name}{item.who.email ? ` | ${item.who.email}` : ' | no email'}</small>
            </article>
          ))}
        </section>
      ) : null}
      <ListSection title="Slide deck changes" items={report.slideDeck} />
      <ListSection title="How to run the session" items={report.presentation} />
      <ListSection title="Keeping them engaged" items={report.engagement} />
    </>
  );
}

function ClusterSection({ title, clusters, showPeople = false }: { title: string; clusters: InsightCluster[]; showPeople?: boolean }) {
  if (!clusters.length) return null;
  return (
    <section className="insight-section">
      <h3>{title}</h3>
      {clusters.map((cluster, index) => (
        <article className="insight-block" key={`${cluster.label || cluster.question || title}-${index}`}>
          <header>
            <strong>{index + 1}. {cluster.label || cluster.question}</strong>
            <span>{formatNumber(cluster.askedTimes)}x{cluster.unanswered ? ` | ${formatNumber(cluster.unanswered)} unanswered` : ''}</span>
          </header>
          {cluster.detail ? <p>{cluster.detail}</p> : null}
          {cluster.variants.map((variant, variantIndex) => <blockquote key={`${variant}-${variantIndex}`}>{variant}</blockquote>)}
          {showPeople && cluster.people.length ? <small>{cluster.people.map(person => person.name).join(', ')}</small> : null}
        </article>
      ))}
    </section>
  );
}

function ListSection({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return <section className="insight-section"><h3>{title}</h3><ul>{items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul></section>;
}

function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

function formatDate(value: number | null): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : dateFormatter.format(date);
}
